import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Swiper, SwiperSlide } from "swiper/react";
import { useDispatch, useSelector } from "react-redux";
import axios from "../helper/axios";
import "swiper/css";
import "swiper/css/navigation";
import "./Productslider.css";

import { Pagination, Navigation } from "swiper";
import ColorSwatch from './Common/ColorSwatch';
import ProductListPrice from './Common/ProductListPrice';
import DiscountBadge from './Common/DiscountBadge';
import ProductItem from "./ProductItem";

const EXCLUDED_LOCATION_SLIDER = "011";

/** Vetëm për faqet male/female: mos shfaq Okaidi as location 011 në slider */
function excludeOkaidiAndBannedLocation(products, pageGender) {
    const g = String(pageGender || "").trim().toLowerCase();
    if (g !== "male" && g !== "female") return products;
    if (!Array.isArray(products) || products.length === 0) return products;

    return products.filter((item) => {
        const brand = String(item?.brand ?? item?.vendor ?? "")
            .trim()
            .toLowerCase();
        if (
            brand === "okaidi" ||
            brand.startsWith("okaidi-") ||
            brand.startsWith("okaidi ")
        ) {
            return false;
        }

        const loc = String(item?.location ?? item?.storeCode ?? "")
            .trim()
            .toLowerCase();
        if (loc === EXCLUDED_LOCATION_SLIDER) return false;
        return true;
    });
}

export default function Productslider(props) {
    const gender = useSelector(state => state?.category?.gender)
    const [allData, setAllData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);
    const [isSwiping, setIsSwiping] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const containerRef = useRef(null);
    const latestRequestRef = useRef(0);
    const loadingRequestRef = useRef(0);
    const abortRef = useRef(null);
    const eager = props.eager === true;
    const prefetchPx = Number.isFinite(Number(props.prefetchPx)) ? Number(props.prefetchPx) : 800;
    const effectivePrefetchPx = isCoarsePointer ? Math.max(prefetchPx, 2400) : prefetchPx;

    const pageGender = props.gender || gender;

    // A stable key so we can refetch correctly when inputs change.
    const fetchKey = useMemo(() => {
        return JSON.stringify({
            gender,
            pg: props.gender,
            start: props.start,
            limit: props.limit,
            type: props.type,
            fallbackType: props.fallbackType,
            dataSource: props.dataSource,
        });
    }, [gender, props.gender, props.start, props.limit, props.type, props.fallbackType, props.dataSource]);

    const spreadProductsByBrand = (products, limit) => {
        if (!Array.isArray(products) || products.length === 0) return [];

        const maxItems = parseInt(limit, 10) || products.length;
        const bucketKey = (item) => {
            const b = (item?.brand ?? item?.vendor ?? "").toString().trim().toLowerCase();
            if (b) return b;
            const loc = (item?.location ?? "").toString().trim().toLowerCase();
            if (loc) return `loc:${loc}`;
            return "_unknown";
        };

        const buckets = new Map();
        const orderedKeys = [];

        products.forEach((item) => {
            const key = bucketKey(item);
            if (!buckets.has(key)) {
                buckets.set(key, []);
                orderedKeys.push(key);
            }
            buckets.get(key).push(item);
        });

        const mixed = [];

        for (const key of orderedKeys) {
            if (mixed.length >= maxItems) break;
            const items = buckets.get(key);
            if (items?.length > 0) mixed.push(items.shift());
        }

        let idx = 0;
        while (mixed.length < maxItems) {
            const key = orderedKeys[idx % orderedKeys.length];
            const items = buckets.get(key);
            if (items?.length > 0) mixed.push(items.shift());

            idx++;
            if (idx >= orderedKeys.length && mixed.length === 0) break;
            if (idx >= orderedKeys.length * 3) {
                const anyLeft = orderedKeys.some((k) => (buckets.get(k)?.length || 0) > 0);
                if (!anyLeft) break;
                idx = 0;
            }
        }

        return mixed;
    };

    const fetchProducts = async (typeValue, signal) => {
        if (props.dataSource === "new-arrivals" || props.dataSource === "top-picks") {
            const requestedLimit = parseInt(props.limit, 10) || 20;
            // Important: keep pool moderate so homepage sliders don't feel "stuck" on mobile.
            // We still mix by brand, but we don't need hundreds of items for a 20-item slider.
            const fetchPoolSize = isCoarsePointer
                ? Math.min(120, Math.max(requestedLimit * 6, 80))
                : Math.min(200, Math.max(requestedLimit * 8, 120));
            const isTopPicks = props.dataSource === "top-picks";
            const scrollboxPayload = {
                brands: [],
                sort: "",
                sizes: [],
                gtPrice: null,
                ltPrice: null,
                catAndGender: (props.gender) || (gender === "life" ? "life" : gender),
                page: 1,
                per_page: fetchPoolSize,
                discount: "",
                discountNo: "",
                selectedGender: (props.gender) || (gender === "life" ? "life" : gender),
                selectedCat: [],
                newIn: !isTopPicks,
                selectionForYou: isTopPicks
            };
            const res = await axios.post("/scrollbox", scrollboxPayload, { signal });
            const raw = res.data;
            const list = Array.isArray(raw) ? raw : (raw?.products || []);
            const products = excludeOkaidiAndBannedLocation(
                list,
                pageGender
            );
            return spreadProductsByBrand(products, requestedLimit);
        }

        const requestedLimit = parseInt(props.limit, 10) || 20;
        const poolLimit = isCoarsePointer
            ? Math.min(120, Math.max(60, requestedLimit * 6))
            : Math.min(200, Math.max(80, requestedLimit * 8));
        const data = {
            gender: (props.gender) || (gender === "life" ? "life" : gender),
            start: props.start,
            limit: poolLimit,
            type: typeValue
        };
        const res = await axios.post("/homeproducts", data, { signal });
        const raw = excludeOkaidiAndBannedLocation(
            Array.isArray(res.data) ? res.data : [],
            pageGender
        );
        return spreadProductsByBrand(raw, requestedLimit);
    };

    const apiData = async () => {
        // Abort any in-flight request so old requests don't keep bandwidth/CPU busy.
        try {
            abortRef.current?.abort?.();
        } catch {}
        const controller = new AbortController();
        abortRef.current = controller;

        const requestId = Date.now();
        latestRequestRef.current = requestId;
        loadingRequestRef.current = requestId;
        setIsLoading(true);
        setErrorMsg("");
        try {
            const primaryData = await fetchProducts(props.type, controller.signal);

            if (latestRequestRef.current !== requestId) return;

            if ((!primaryData || primaryData.length === 0) && props.fallbackType) {
                const fallbackData = await fetchProducts(props.fallbackType, controller.signal);
                if (latestRequestRef.current !== requestId) return;
                setAllData(fallbackData);
                return;
            }

            setAllData(primaryData);
        } catch (error) {
            // Ignore abort errors (expected during fast navigation / refetch)
            const isAbort =
                error?.name === "CanceledError" ||
                error?.name === "AbortError" ||
                error?.code === "ERR_CANCELED";
            if (isAbort) return;

            console.error("API call failed:", error);
            if (latestRequestRef.current !== requestId) return;
            setErrorMsg("Gabim gjatë ngarkimit të produkteve.");
            setAllData([]);
        } finally {
            // Prevent "stuck loading" if request ordering changes.
            if (loadingRequestRef.current === requestId) setIsLoading(false);
        }
    };

    // When inputs change, clear previous data and abort old requests.
    useEffect(() => {
        setAllData([]);
        setErrorMsg("");
        setIsLoading(false);
        try {
            abortRef.current?.abort?.();
        } catch {}
    }, [fetchKey]);

    // Detect touch-like pointers (mobile/tablet). Keep it out of render hot paths.
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mq = window.matchMedia("(pointer: coarse)");
        const update = () => setIsCoarsePointer(!!mq.matches);
        update();
        if (typeof mq.addEventListener === "function") mq.addEventListener("change", update);
        else if (typeof mq.addListener === "function") mq.addListener(update);
        return () => {
            if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", update);
            else if (typeof mq.removeListener === "function") mq.removeListener(update);
        };
    }, []);

    // iOS Safari: prefer Swiper cssMode (native scrolling) for smoothness.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const ua = window.navigator?.userAgent || "";
        const isAppleMobile = /iP(hone|ad|od)/.test(ua);
        setIsIOS(isAppleMobile);
    }, []);

    // Start loading only when slider is near viewport (prevents heavy requests at page load).
    useEffect(() => {
        if (hasStarted) return;
        if (eager) {
            setHasStarted(true);
            return;
        }
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            setHasStarted(true);
            return;
        }

        const obs = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setHasStarted(true);
                }
            },
            // Mobile: start earlier to avoid "wait a few seconds" feeling.
            { root: null, rootMargin: `${effectivePrefetchPx}px 0px`, threshold: 0.01 }
        );

        obs.observe(el);
        return () => obs.disconnect();
    }, [hasStarted, eager, effectivePrefetchPx]);

    // Fetch when started, and refetch when key changes (e.g. gender/page changes).
    useEffect(() => {
        if (!hasStarted) return;
        apiData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasStarted, fetchKey]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            try {
                abortRef.current?.abort?.();
            } catch {}
        };
    }, []);


    return (
        <div ref={containerRef} className="flex container mb-9 mx-auto 1st">
            <div className={`HomePageSlideShow__wrapper bg-gray-100${isSwiping ? " is-swiping" : ""}`}>
                <div className="HomePageSlideShowbox container items-center justify-center mx-auto">
                    {isLoading ? (
                        <div className="py-10 text-center text-sm text-gray-500">
                            Duke ngarkuar produktet...
                        </div>
                    ) : errorMsg ? (
                        <div className="py-10 text-center text-sm text-red-600">
                            {errorMsg}
                        </div>
                    ) : allData && allData.length > 0 ? (
                        <Swiper
                            slidesPerView={2}
                            slidesPerGroup={1}
                            slidesPerGroupSkip={0}
                            longSwipes={false}
                            shortSwipes={true}
                            threshold={12}
                            spaceBetween={30}
                            loop={allData.length >= 10 && !(isIOS && isCoarsePointer)}
                            navigation={true}
                            // iOS touch: use native scrolling inside Swiper for smoothness
                            cssMode={isIOS && isCoarsePointer}
                            passiveListeners={isCoarsePointer}
                            touchStartPreventDefault={isCoarsePointer ? false : undefined}
                            onTouchStart={() => setIsSwiping(true)}
                            onTouchEnd={() => setIsSwiping(false)}
                            onTransitionEnd={() => setIsSwiping(false)}
                            breakpoints={{
                                0: { slidesPerView: 2, spaceBetween: 0 },
                                480: { slidesPerView: 2, spaceBetween: 20 },
                                640: { slidesPerView: 2, spaceBetween: 20 },
                                768: { slidesPerView: 2, spaceBetween: 20 },
                                1024: { slidesPerView: 4, spaceBetween: 30 },
                                1280: { slidesPerView: 4, spaceBetween: 30 },
                                1536: { slidesPerView: 5, spaceBetween: 30 },
                            }}
                            modules={[Pagination, Navigation]}
                            className="mySwiper"
                        >
                            {allData.map((dt) => {
                                return (
                                    <SwiperSlide key={dt._id}>
                                        <ProductItem item={dt} hideFavorite />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>
                    ) : (
                        <div className="py-10 text-center text-sm text-gray-500">
                            Nuk ka produkte për momentin.
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
